import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Typography,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import Title from './Title';

const ImageGeneration = ({ user, accessToken }) => {
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [seed, setSeed] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [loading, setLoading] = useState(false);
  const [fetchingImages, setFetchingImages] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const sizeOptions = [
    { value: '1024x1024', label: '1024x1024 (Square)' },
    { value: '1216x832', label: '1216x832 (Landscape)' },
    { value: '896x1152', label: '896x1152 (Portrait)' }
  ];

  useEffect(() => {
    fetchGeneratedImages();
  }, [user.id]);

  const fetchGeneratedImages = async () => {
    setFetchingImages(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_API_BASE_URL + 'actions/get_generated_images',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ access_token: accessToken })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail);
      }
      setImages(data.images || []);
    } catch (error) {
      showSnackbar('Error fetching images: ' + error.message, 'error');
    } finally {
      setFetchingImages(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      showSnackbar('Please enter a prompt', 'warning');
      return;
    }

    if (seed.trim() !== '') {
      const seedValue = parseInt(seed, 10);
      if (isNaN(seedValue) || seedValue < 0) {
        showSnackbar('Please enter a valid positive number for seed', 'warning');
        return;
      }
    }

    setLoading(true);
    try {
      const seedValue = seed.trim() !== '' ? parseInt(seed, 10) : -1;
      const response = await fetch(
        process.env.REACT_APP_API_BASE_URL + 'actions/generate_image',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_key: user.id,
            prompt: prompt,
            seed: seedValue,
            size: size
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate image');
      }

      showSnackbar('Image generated successfully!', 'success');
      setPrompt('');
      await fetchGeneratedImages();
      setPage(0);
    } catch (error) {
      showSnackbar('Error generating image: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event, image) => {
    event.stopPropagation();
    setImageToDelete(image);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;

    try {
      const response = await fetch(
        process.env.REACT_APP_API_BASE_URL + 'actions/delete_image',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            access_token: accessToken,
            image_key: imageToDelete.filename
          })
        }
      );

      const result = await response.json();

      if (result.status === "Success") {
        showSnackbar('Image deleted successfully', 'success');
        setImages(images.filter(img => img.filename !== imageToDelete.filename));
        if (selectedImage?.filename === imageToDelete.filename) {
          handleCloseImageDialog();
        }
      } else {
        showSnackbar('Failed to delete image: ' + result.error, 'error');
      }
    } catch (error) {
      showSnackbar('Error deleting image: ' + error.message, 'error');
    } finally {
      setOpenDeleteDialog(false);
      setImageToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setImageToDelete(null);
  };

  const handleDownloadImage = (image) => {
    const link = document.createElement('a');
    link.href = process.env.REACT_APP_API_BASE_URL + image.url;
    link.download = image.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewImage = (image) => {
    setSelectedImage(image);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const displayImages = images.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <Title>Image Generation</Title>
      <Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Describe the image you want to generate..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateImage}
            disabled={loading || !prompt.trim()}
            sx={{ minWidth: 120, height: 56 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Generate'}
          </Button>
        </Box>
      </Box>

      <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mb: 1 }}
        >
          Advanced Settings
      </Button>


      <Collapse in={showAdvanced}>
        <Box sx={{ borderRadius: 1, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Image Size</InputLabel>
                <Select
                  value={size}
                  label="Image Size"
                  onChange={(e) => setSize(e.target.value)}
                  disabled={loading}
                >
                  {sizeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Seed (optional)"
                placeholder="Leave empty to use a random seed"
                type="number"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <Title>Your Image Library</Title>

      {fetchingImages ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : images.length === 0 ? (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
          No images generated yet. Enter a prompt above to create your first image!
        </Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            {displayImages.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={process.env.REACT_APP_API_BASE_URL + image.url}
                    alt={image.filename}
                    sx={{ 
                      objectFit: 'cover',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleViewImage(image)}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', px: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(image.created_at).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadImage(image)}
                        title="Download"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(e, image)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TablePagination
            component="div"
            count={images.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[8, 12, 24, 48]}
          />
        </>
      )}

      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
        >
        <DialogTitle sx={{ position: 'relative', pr: 6 }}>
            Image Preview
            <Typography variant="caption" display="block" color="text.secondary">
            {selectedImage && new Date(selectedImage.created_at).toLocaleString()}
            </Typography>
            <IconButton
            onClick={handleCloseImageDialog}
            sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
            }}
            >
            <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent>
            {selectedImage && (
            <Box
                component="img"
                src={process.env.REACT_APP_API_BASE_URL + selectedImage.url}
                alt={selectedImage.filename}
                sx={{
                width: '100%',
                height: 'auto',
                display: 'block'
                }}
            />
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => handleDownloadImage(selectedImage)} startIcon={<DownloadIcon />}>
            Download
            </Button>
            <Button onClick={(e) => handleDeleteClick(e, selectedImage)} startIcon={<DeleteIcon />}>
            Delete
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ImageGeneration;